import { Input } from "../../atom/Input";
import { Button } from "../../atom/Button";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, View, ViewOff } from "@carbon/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ROUTES } from "../../../utils/consts/routes";
import { successToast } from "../../../utils/toast";
import { updatePassword } from "../../../services/apiHelpers";
import { validateEmail } from "../../../utils/validations";
import { useAuth } from "../../../utils/contexts/AuthProvider";
import { useUserContext } from "../../../utils/contexts/UserProvider";

export const ResetPassword = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { setUser } = useUserContext();

  const onEmailBlur = () => {
    if (!validateEmail(email)) {
      setErrorMessage(t("Invalid email format"));
    } else {
      setErrorMessage("");
    }
  };

  const onPasswordBlur = () => {
    const pass = password.trim();
    const confirmPass = confirmPassword.trim();

    if (pass === "") {
      setErrorMessage(t("Password cannot be empty"));
      return;
    }

    if (confirmPass === "") {
      setErrorMessage(t("Confirmed password cannot be empty"));
      return;
    }

    if (pass.length < 8) {
      setErrorMessage(t("Password requirements not met"));
      return;
    }

    if (pass.length > 32) {
      setErrorMessage(t("Password requirements not met"));
      return;
    }

    if (!pass.match(/\p{L}/gu)) {
      setErrorMessage(t("Password requirements not met"));
      return;
    }

    if (pass !== confirmPass) {
      setErrorMessage(t("Passwords do not match"));
      return;
    }

    setErrorMessage("");
  };

  const handleConfirm = async () => {
    const token = params.get("token");

    if (errorMessage || !email || !token) {
      return;
    }

    const success: { data: string | { error: string } } = await updatePassword({
      email,
      password,
      password_confirmation: confirmPassword,
      passwordToken: token,
    });

    if (typeof success?.data !== "string") {
      setErrorMessage(success?.data?.error);
      return;
    }

    setUser(null);
    auth.logout();

    navigate(ROUTES.home);
    successToast(t("Password reset successfully"));
  };

  return (
    <div className="flex items-center justify-center bg-white">
      <div className="flex flex-col justify-center items-center min-w-[360px] max-w-[360px] mt-24 gap-8">
        <div className="flex flex-col justify-center items-center gap-[120px]">
          <img src="/images/logo.svg" className="h-[24px]" alt="" />
          <div className="flex flex-col justify-center items-center gap-3">
            <p className="font-semibold text-[32px] leading-[40.32px] tracking-[-0.1px] text-black">
              {t("Reset Password")}
            </p>
            <p className="font-light text-[16px] leading-[22.4px] tracking-[0.2px] text-gray-550">
              {t("Enter your new password to reset it")}
            </p>
          </div>
        </div>
        <div className="flex flex-col justify-center items-center w-full gap-6">
          <div className="w-full">
            <Input
              id="email"
              type="email"
              value={email}
              placeholder={t("Enter your email")}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={onEmailBlur}
              inputClassName="rounded-b-none"
            />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              placeholder={t("Enter new password")}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={onPasswordBlur}
              icon={showPassword ? <ViewOff size={16} /> : <View size={16} />}
              onIconClick={() => setShowPassword((prev) => !prev)}
              inputClassName="rounded-none"
            />
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              placeholder={t("Confirm new password")}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={onPasswordBlur}
              icon={showConfirmPassword ? <ViewOff size={16} /> : <View size={16} />}
              onIconClick={() => setShowConfirmPassword((prev) => !prev)}
              inputClassName="rounded-t-none"
            />
            {errorMessage && (
              <p className="text-red-500 font-normal text-[14px] leading-[19.6px] tracking-[0px] mt-1">
                {errorMessage}
              </p>
            )}
          </div>
          <div className="self-start">
            <p className="text-[16px]">{t("Password requirements:")}</p>
            <ul className="text-sm text-gray-375 leading-[22.4px] tracking-[0.2px]">
              <li>- {t("Minimum 8 characters")}</li>
              <li>- {t("Maximum 32 characters")}</li>
              <li>- {t("Must contain letters")}</li>
            </ul>
          </div>
          <div className="w-full">
            <Button
              type="primary"
              label={t("Reset Password")}
              onClick={handleConfirm}
              leftIcon={<ArrowRight size={20} />}
              additionalClassName="flex items-center justify-center w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
